import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { COLLECTION_URL, IMAGES } from "@/lib/site";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.05,
      delay: 0.35 + i * 0.14,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.24]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      <motion.img
        src={IMAGES.heroMoto}
        alt="Adventure motorcycle kitted for the wild on a dramatic mountain pass"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <motion.div
        style={{ opacity: overlayO }}
        className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/30 to-[#050505]"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 to-transparent" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-16 sm:px-8 sm:pb-20"
      >
        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={0.15}
          className="mb-5 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#ee1111]" />
          <span className="font-cond text-xs font-semibold uppercase tracking-[0.32em] text-[#ee1111] sm:text-sm">
            Precision Motorcycle Parts
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.82] text-white">
          {["Own Your", "Adventure"].map((t, i) => (
            <span key={t} className="block overflow-hidden">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={i}
                className="block text-[17vw] sm:text-[15vw] lg:text-[12.5vw]"
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-7 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1.05}
            className="max-w-md text-sm text-white/70 sm:text-base"
          >
            Rugged, source-built parts for riders who go further — crash
            protection, lighting, ergonomics and tools engineered for your
            machine.
          </motion.p>

          <motion.a
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1.15}
            data-testid="hero-shop-btn"
            href={COLLECTION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex w-fit items-center gap-3 rounded-full bg-[#ee1111] px-7 py-4 font-cond text-sm font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-[#cc0f0f] hover:shadow-[0_0_40px_-8px_#ee1111]"
          >
            Shop the Collection
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </motion.a>
        </div>
      </motion.div>

      <motion.div
        variants={fade}
        initial="hidden"
        animate="show"
        custom={1.5}
        className="absolute bottom-6 right-5 z-10 hidden items-center gap-2 text-white/40 sm:right-8 sm:flex"
      >
        <ArrowDown size={14} className="animate-bounce" />
        <span className="font-cond text-xs uppercase tracking-[0.25em]">
          Scroll
        </span>
      </motion.div>
    </section>
  );
};
